'use client';

import { LAYERS, type LayerName, RECONCILED } from '@slims/shared/energy';
import { useState } from 'react';
import { PodLayerColors, PodLayerLabels } from '../../charts';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '../../molecules/dialog/dialog';

/**
 * L'esploso per POD di un KPI o di un grafico: la classifica dietro il totale.
 *
 * È la vista generica su cui si aprono tutti i pannelli dell'app, quindi non sa
 * nulla della metrica che sta mostrando oltre a quello che le viene passato —
 * etichetta, unità, righe. Quello che sa fare, e che nessun pannello dovrebbe
 * rifare da sé, è dichiarare **la natura del numero**:
 *
 * - da quale base di riconciliazione viene (le due basi non danno sempre lo
 *   stesso livello vincente, e sapere quale si sta guardando è la differenza fra
 *   un dettaglio e un numero che sembra sbagliato);
 * - se è misurato o ripartito secondo una convenzione;
 * - se la somma delle righe torna il totale del pannello da cui si è aperto.
 *
 * Riceve dati e riporta intenzione: nessuna chiamata, nessuno store.
 */

export type MetricExplodeDecomposition = 'sum' | 'allocated';

export type MetricExplodeLayerTotals = Partial<Record<LayerName, number>>;

export type MetricExplodeLayer = LayerName | typeof RECONCILED;

export interface MetricExplodeRow {
	podId: string;
	/** Codice POD del punto di connessione. */
	codice: string;
	memberName: string | null;
	/** Valore della metrica per questo POD, nell'unità della metrica. */
	value: number;
	/** Quota sul totale 0-100, o `null` dove una quota non ha senso (%, medie). */
	pct: number | null;
	/** Il livello che ha vinto la riconciliazione su questo POD. */
	topLayer: LayerName | null;
	layers: MetricExplodeLayerTotals;
	/** Slot coperti da ciascun livello, sulla stessa finestra di `coverage.total`. */
	layerCoverage?: MetricExplodeLayerTotals;
	coverage: { covered: number; total: number };
	/** Valori accessori, es. le tre fasce orarie. Mostrati come nota di riga. */
	extra?: Record<string, number>;
}

export interface MetricExplodeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Titolo dell'esploso — di norma il nome della metrica. */
	label: string;
	unit: string;
	/** Intervallo già formattato per chi legge, es. «luglio 2026». */
	periodo: string;
	decomposition: MetricExplodeDecomposition;
	rows: MetricExplodeRow[];
	/** Σ delle righe, calcolata dal server sulla base della metrica. */
	total: number;
	/**
	 * Il totale che il pannello sta mostrando, se lo conosce. Serve al piede di
	 * quadratura: uno scostamento va detto, non nascosto.
	 */
	kpiTotal?: number | null;
	/** La convenzione di riparto, obbligatoria quando `decomposition` è 'allocated'. */
	allocationBasis?: string;
	/** Nota della metrica, es. «previsione, non misura». */
	note?: string;
	loading?: boolean;
	/** Motivo per cui non ci sono righe, quando la scomposizione è possibile. */
	emptyReason?: string;
	/**
	 * `false` dove il valore non è la somma di un livello (derivazioni, quote
	 * ripartite, conteggi): il selettore resta visibile ma spento, perché
	 * nasconderlo su alcuni pannelli e non su altri sembrerebbe un difetto.
	 */
	layerFilterEnabled?: boolean;
	/** Perché il filtro è spento. Obbligatorio quando `layerFilterEnabled` è falso. */
	layerFilterReason?: string;
	/** Apre il dettaglio del POD: produzione e consumo per livello. */
	onSelectPod?: (podId: string) => void;
}

/**
 * Il selettore: la risultante, poi i quattro livelli nell'ordine di priorità.
 *
 * Generato da `LAYERS` e non riscritto a mano — è così che le etichette erano
 * andate in deriva rispetto alla base su cui giravano davvero, mostrando la
 * bolletta come livello a sé e la tipica come se non fosse un livello.
 */
const LAYER_TABS: { value: MetricExplodeLayer; label: string; hint: string }[] = [
	{ value: RECONCILED, label: 'Riconciliato', hint: 'Il valore che il KPI ha usato' },
	...LAYERS.map((l) => ({ value: l.name as MetricExplodeLayer, label: l.label, hint: l.hint })),
];

/** L'ordine di priorità, scritto una volta per il suggerimento del piede. */
const PRIORITY_HINT = `Priorità: ${LAYERS.map((l) => l.label).join(' › ')}`;

/**
 * La stessa classifica riletta su un singolo livello.
 *
 * Un POD senza quel livello resta in lista **a zero** invece di sparire: è la
 * differenza fra «il GSE non ha ancora certificato questo POD» e «questo POD non
 * esiste», e su una classifica che si usa per cercare i buchi è tutta la
 * domanda. Per lo stesso motivo la copertura mostrata diventa quella del livello
 * scelto, non quella della riconciliazione.
 */
export function viewByLayer(
	rows: MetricExplodeRow[],
	layer: MetricExplodeLayer,
): { rows: MetricExplodeRow[]; total: number } {
	if (layer === RECONCILED) {
		return { rows, total: rows.reduce((a, r) => a + r.value, 0) };
	}
	const mapped = rows.map((r) => ({
		...r,
		value: r.layers[layer] ?? 0,
		coverage: { covered: r.layerCoverage?.[layer] ?? 0, total: r.coverage.total },
	}));
	const total = mapped.reduce((a, r) => a + r.value, 0);
	return {
		rows: mapped
			.map((r) => ({ ...r, pct: total === 0 ? null : (r.value / total) * 100 }))
			.sort((a, b) => Math.abs(b.value) - Math.abs(a.value)),
		total,
	};
}

function fmt(value: number, unit: string): string {
	const digits = unit === '€' ? 2 : unit === '%' ? 1 : unit === 'n' ? 0 : 1;
	return `${value.toLocaleString('it-IT', {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	})} ${unit === 'n' ? '' : unit}`.trim();
}

/** Pastiglia del livello vincente, con il colore che il grafico usa per quel livello. */
function LayerChip({
	layer,
	absent,
}: {
	layer: keyof MetricExplodeLayerTotals;
	/** Il livello richiesto non copre nulla su questo POD: lo zero ha una causa. */
	absent?: boolean;
}) {
	return (
		<span
			className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm bg-bg-secondary px-1.5 py-0.5 text-[10px] text-fg-secondary"
			style={absent ? { opacity: 0.55 } : undefined}
		>
			<span
				className="size-2 shrink-0 rounded-sm"
				style={{ backgroundColor: PodLayerColors[layer] }}
				aria-hidden
			/>
			{PodLayerLabels[layer]}
			{absent && ' · assente'}
		</span>
	);
}

/** Barra di copertura: quanti slot dell'intervallo hanno un dato. */
function CoverageBar({ covered, total }: { covered: number; total: number }) {
	const pct = total > 0 ? Math.round((covered / total) * 100) : 0;
	return (
		<span className="inline-flex items-center gap-1.5" title={`${covered}/${total} slot`}>
			<span className="h-1 w-10 overflow-hidden rounded-full bg-bg-secondary">
				<span
					className="block h-full rounded-full bg-accent"
					style={{ width: `${pct}%` }}
					aria-hidden
				/>
			</span>
			<span className="text-[10px] text-fg-secondary tabular-nums">{pct}%</span>
		</span>
	);
}

export function MetricExplodeDialog({
	open,
	onOpenChange,
	label,
	unit,
	periodo,
	decomposition,
	rows,
	total,
	kpiTotal,
	allocationBasis,
	note,
	loading,
	emptyReason,
	layerFilterEnabled = true,
	layerFilterReason,
	onSelectPod,
}: MetricExplodeDialogProps) {
	const [layer, setLayer] = useState<MetricExplodeLayer>(RECONCILED);
	// Il dialogo resta montato mentre cambia il pannello da cui lo si apre: senza
	// questo, il livello scelto su un KPI si porterebbe dietro sul successivo,
	// dove magari non si applica nemmeno.
	const [seenLabel, setSeenLabel] = useState(label);
	if (label !== seenLabel) {
		setSeenLabel(label);
		setLayer(RECONCILED);
	}
	const active: MetricExplodeLayer = layerFilterEnabled ? layer : RECONCILED;
	// Sul riconciliato il totale resta quello che il server ha calcolato sulla
	// base della metrica: è Σ delle righe, ma è anche il numero che il piede di
	// quadratura confronta col KPI, e ricalcolarlo qui lo scollegherebbe.
	const view = active === RECONCILED ? { rows, total } : viewByLayer(rows, active);
	const activeLabel = active === RECONCILED ? null : PodLayerLabels[active];

	// Lo scostamento fra la somma delle righe e il numero del pannello. Sotto un
	// centesimo è arrotondamento e non vale una riga di avviso. Su un livello
	// singolo non si confronta affatto: lì la differenza dal KPI è la domanda che
	// il filtro serve a porre, non un difetto da segnalare.
	const delta = kpiTotal != null && active === RECONCILED ? view.total - kpiTotal : null;
	const mismatch = delta != null && Math.abs(delta) > 0.01;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-3xl">
				<DialogHeader>
					<DialogTitle className="text-base">{label} · per POD</DialogTitle>
					<DialogDescription>
						{periodo} · {rows.length} POD
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-3">
					<div className="flex flex-wrap items-center gap-1.5">
						<span
							className="rounded-sm bg-bg-secondary px-1.5 py-0.5 text-[10px] text-fg-secondary"
							title={PRIORITY_HINT}
						>
							{PRIORITY_HINT}
						</span>
						{decomposition === 'allocated' && (
							<span className="rounded-sm bg-warn/10 px-1.5 py-0.5 text-[10px] text-warn">
								quota allocata
							</span>
						)}
						{note && <span className="text-[11px] text-fg-secondary">{note}</span>}
					</div>

					{decomposition === 'allocated' && allocationBasis && (
						<p className="rounded-md bg-bg-secondary px-2.5 py-1.5 text-[11px] text-fg-secondary">
							Questo numero non è misurato sul POD: {allocationBasis}.
						</p>
					)}

					<div className="flex flex-wrap items-center gap-2">
						<fieldset
							className="flex flex-wrap gap-1 border-0 p-0"
							aria-label="Livello di dato energetico"
							disabled={!layerFilterEnabled}
						>
							{LAYER_TABS.map((t) => (
								<button
									key={t.value}
									type="button"
									onClick={() => setLayer(t.value)}
									aria-pressed={t.value === active}
									title={layerFilterEnabled ? t.hint : layerFilterReason}
									className={
										'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-40 ' +
										(t.value === active
											? 'border-accent bg-accent/10 text-accent'
											: 'border-border-secondary text-fg-secondary hover:text-fg')
									}
								>
									{t.value !== RECONCILED && (
										<span
											className="size-2 shrink-0 rounded-sm"
											style={{ backgroundColor: PodLayerColors[t.value] }}
											aria-hidden
										/>
									)}
									{t.label}
								</button>
							))}
						</fieldset>
						{!layerFilterEnabled && layerFilterReason && (
							<span className="text-[11px] text-fg-secondary">{layerFilterReason}</span>
						)}
					</div>

					{activeLabel && (
						<p className="rounded-md bg-bg-secondary px-2.5 py-1.5 text-[11px] text-fg-secondary">
							Lettura sul livello «{activeLabel}»: dove non copre l'intervallo il valore è 0, e la
							somma non deve tornare il totale del pannello - quello usa il riconciliato.
						</p>
					)}

					{loading ? (
						<div className="flex h-40 items-center justify-center text-fg-secondary text-sm">
							Caricamento della scomposizione…
						</div>
					) : rows.length === 0 ? (
						<div className="flex h-40 items-center justify-center px-6 text-center text-fg-secondary text-sm">
							{emptyReason ?? 'Nessun POD con dati in questo intervallo.'}
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full min-w-[34rem] border-collapse text-sm">
								<thead>
									<tr className="border-border-secondary border-b text-[11px] text-fg-secondary">
										<th className="py-1.5 pr-2 text-left font-normal">POD</th>
										<th className="py-1.5 pr-2 text-right font-normal">
											{activeLabel ? `Valore · ${activeLabel}` : 'Valore'}
										</th>
										<th className="py-1.5 pr-2 text-right font-normal">Quota</th>
										<th className="py-1.5 pr-2 text-left font-normal">Livello</th>
										<th className="py-1.5 text-left font-normal">Copertura</th>
									</tr>
								</thead>
								<tbody>
									{view.rows.map((r) => {
										const interactive = onSelectPod != null;
										return (
											<tr
												key={r.podId}
												onClick={interactive ? () => onSelectPod(r.podId) : undefined}
												tabIndex={interactive ? 0 : undefined}
												role={interactive ? 'button' : undefined}
												aria-label={
													interactive
														? `Apri produzione e consumo per livello del POD ${r.codice}`
														: undefined
												}
												onKeyDown={
													interactive
														? (e) => {
																if (e.key === 'Enter' || e.key === ' ') {
																	e.preventDefault();
																	onSelectPod(r.podId);
																}
															}
														: undefined
												}
												className={
													'border-border-secondary/60 border-b last:border-0' +
													(interactive
														? ' cursor-pointer hover:bg-bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent'
														: '')
												}
											>
												<td className="py-2 pr-2">
													<span className="block font-mono text-[12px] text-fg">{r.codice}</span>
													<span className="block text-[11px] text-fg-secondary">
														{r.memberName ?? 'socio non assegnato'}
													</span>
													{r.extra && (
														<span className="block text-[10px] text-fg-secondary tabular-nums">
															{Object.entries(r.extra)
																.map(([k, v]) => `${k} ${fmt(v, unit)}`)
																.join(' · ')}
														</span>
													)}
												</td>
												<td className="py-2 pr-2 text-right font-medium text-fg tabular-nums">
													{fmt(r.value, unit)}
												</td>
												<td className="py-2 pr-2 text-right text-fg-secondary tabular-nums">
													{r.pct == null ? '-' : `${r.pct.toFixed(1)}%`}
												</td>
												<td className="py-2 pr-2">
													{active !== 'riconciliato' ? (
														<LayerChip layer={active} absent={r.coverage.covered === 0} />
													) : r.topLayer ? (
														<LayerChip layer={r.topLayer} />
													) : (
														'-'
													)}
												</td>
												<td className="py-2">
													<CoverageBar covered={r.coverage.covered} total={r.coverage.total} />
												</td>
											</tr>
										);
									})}
								</tbody>
								<tfoot>
									<tr className="border-border-secondary border-t">
										<td className="py-2 pr-2 text-[11px] text-fg-secondary">Somma delle righe</td>
										<td className="py-2 pr-2 text-right font-semibold text-fg tabular-nums">
											{fmt(view.total, unit)}
										</td>
										{/* Le tre colonne di destra non hanno un totale che significhi
										    qualcosa: sommare quote, livelli vincenti e coperture darebbe
										    numeri senza lettura. */}
										<td aria-hidden colSpan={3} />
									</tr>
								</tfoot>
							</table>
						</div>
					)}

					{mismatch && (
						<p className="rounded-md bg-warn/10 px-2.5 py-1.5 text-[11px] text-warn">
							La somma per POD differisce di {fmt(delta, unit)} dal totale del pannello (
							{fmt(kpiTotal!, unit)}). Le due letture non usano lo stesso insieme di misuratori
							oppure la stessa base di riconciliazione.
						</p>
					)}
					{onSelectPod && rows.length > 0 && (
						<p className="text-[11px] text-fg-secondary">
							Apri un POD per vedere produzione e consumo, ciascuno spezzato nei livelli.
						</p>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
